import { NormalizedNumber } from '@sparkdotfi/common-universal'
import { useState } from 'react'

import { LiquidityLineChart } from './components/LiquidityLineChart'
import { LiquidityPieChart } from './components/LiquidityPieChart'
import { LiquidityChartItem } from './types'
import { Token } from '@/domain/types/Token'
import { ExternalLink } from '@/ui/atoms/link/Link'
import { ChartTabComponentProps } from '@/ui/charts/components/ChartTabsPanel'
import { links } from '@/ui/constants/links'
import { cn } from '@/ui/utils/style'

export interface LiquidityChartProps extends ChartTabComponentProps {
  items: LiquidityChartItem[]
  underlyingToken: Token
  immediateLiquidity: NormalizedNumber | undefined
  systemAvailableLiquidity?: NormalizedNumber
}

export function LiquidityChart({
  items,
  immediateLiquidity,
  systemAvailableLiquidity,
  underlyingToken,
}: LiquidityChartProps) {
  const [highlightedIndex, setHighlightedIndex] = useState<number | undefined>(undefined)
  const showImmediateLiquidityInfo = systemAvailableLiquidity !== undefined && immediateLiquidity !== undefined

  return (
    <div className="flex flex-col gap-4 sm:p-4 xl:px-14 xl:py-6">
      <div
        className={cn(
          'grid grid-rows-1 items-start gap-4 md:items-center',
          'sm:grid-cols-[4fr_5fr] sm:grid-rows-1 sm:gap-8',
          'xl:grid-cols-[2fr_3fr] xl:gap-20',
        )}
      >
        <LiquidityPieChart
          className="hidden md:block"
          items={items}
          highlightedIndex={highlightedIndex}
          setHighlightedIndex={setHighlightedIndex}
        />
        <LiquidityLineChart
          items={items}
          highlightedIndex={highlightedIndex}
          setHighlightedIndex={setHighlightedIndex}
        />
      </div>

      <div className="typography-body-3 text-secondary">
        {showImmediateLiquidityInfo ? (
          <>
            Immediate liquidity available for withdrawals is{' '}
            <span className="text-primary">
              {underlyingToken.format(immediateLiquidity, { style: 'compact' })} {underlyingToken.symbol}
            </span>
            . The Spark Liquidity Layer will refill liquidity in the next block if needed. Liquidity display may be
            delayed by <span className="whitespace-nowrap text-primary">5-10 minutes</span>.{' '}
            <span className="whitespace-nowrap">
              <ExternalLink to={links.docs.sparkLiquidityLayer} variant="secondary">
                Learn more
              </ExternalLink>
            </span>
          </>
        ) : (
          <>
            Spark Liquidity Layer maintains the target liquidity for each vault. More capital will move in automatically
            when you withdraw a large amount. Liquidity display may be delayed by{' '}
            <span className="whitespace-nowrap text-primary">5-10 minutes</span>.{' '}
            {import.meta.env.VITE_BASE_MINI_APP !== '1' && (
              <span className="whitespace-nowrap">
                <ExternalLink to={links.docs.sparkLiquidityLayer}>Learn more</ExternalLink>
              </span>
            )}
          </>
        )}
      </div>
    </div>
  )
}
